import { createContext, PropsWithChildren, useMemo, ReactElement, useContext } from 'react';
import { useUserQuery } from 'app/data/queries/use-user-query';
import { User } from 'app/data/types/user';
import { useSdkData } from 'app/shared/hooks/use-sdk-data/use-sdk-data.hook';
import { ErrorNotification } from 'app/shared/types/errors.types';

interface UserContextValue {
  user: User | null;
  isLoading: boolean;
  error: ErrorNotification | null;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  const { sdkUser } = useSdkData();
  const { user, isLoading, error } = useUserQuery(sdkUser);

  const contextValue = useMemo(() => ({ user, isLoading, error }), [user, isLoading, error]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
